import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ListIcon } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@noor/ui';
import { QUICK_MESSAGE_CATEGORIES } from '../config/quick-messages';

interface QuickMessagesPopoverProps {
  disabled?: boolean;
  /** Put the picked reply into the draft (replaces it, like cpanel). */
  onPick: (message: string) => void;
}

/** Categorized canned replies (cpanel's list button next to "send"). */
export function QuickMessagesPopover({
  disabled = false,
  onPick,
}: QuickMessagesPopoverProps) {
  const { t } = useTranslation('inbox');
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <IconButton
          type="button"
          variant="ghost"
          size="sm"
          className="rounded-full"
          aria-label={t('composer.quickMessages')}
          disabled={disabled}
        >
          <ListIcon className="size-5" />
        </IconButton>
      </PopoverTrigger>
      <PopoverContent side="top" align="end" className="w-[360px] p-2">
        <Accordion type="single" collapsible className="max-h-[420px] overflow-y-auto">
          {QUICK_MESSAGE_CATEGORIES.map((category) => (
            <AccordionItem key={category.id} value={category.id}>
              <AccordionTrigger className="px-2 py-2 text-sm font-semibold">
                {t(`quick.categories.${category.id}`)}
              </AccordionTrigger>
              <AccordionContent className="flex flex-col gap-0.5 px-1 pb-2">
                {category.items.map((text) => (
                  <button
                    key={text}
                    type="button"
                    className="rounded-lg px-3 py-2 text-left text-[13px] leading-snug text-muted-foreground transition-colors hover:bg-primary hover:text-primary-foreground focus-visible:ring-[3px] focus-visible:ring-ring/40 focus-visible:outline-none"
                    onClick={() => {
                      onPick(text);
                      setOpen(false);
                    }}
                  >
                    {text}
                  </button>
                ))}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </PopoverContent>
    </Popover>
  );
}
